import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Totals for a list page, fetched over the whole filtered set rather than the visible page. Refetches
 * whenever `deps` change - pass the filters, never the paging - and keeps showing the previous
 * figures while the next ones load, so the bar does not blink on every keystroke in a filter.
 */
export const useListSummary = <T>(fetchSummary: () => Promise<T>, deps: unknown[]) => {
  const [summary, setSummary] = useState<T>();
  const [loading, setLoading] = useState(false);
  // Only the newest request may write: a slow answer to an old filter must not overwrite a fast
  // answer to the current one.
  const latest = useRef(0);

  const load = useCallback(async () => {
    latest.current += 1;
    const id = latest.current;
    setLoading(true);
    try {
      const result = await fetchSummary();
      if (id === latest.current) setSummary(result);
    } catch (error) {
      // The bar is supplementary; a failed total must not break the table beside it.
      console.error(error);
    } finally {
      if (id === latest.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    load();
    return () => {
      latest.current += 1;
    };
  }, [load]);

  return { summary, loading, refresh: load };
};
